import React, { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import AnimatedWrapper from "../components/AnimatedWrapper";
import { useNavigate } from "react-router-dom";
import { steps, reviews } from "../config/data";
import amplitude from "../utils/amplitude";
import { motion } from "framer-motion";

export function StarRating({ rating, max = 5 }) {
  const fullStars = Math.floor(rating);
  const halfStar = rating % 1 >= 0.25 && rating % 1 < 0.75;
  const emptyStars = max - fullStars - (halfStar ? 1 : 0);

  return (
    <div className="flex text-yellow-400 text-2xl">
      {Array(fullStars)
        .fill("★")
        .map((s, i) => (
          <span key={`full-${i}`}>{s}</span>
        ))}
      {halfStar && <span>⯨</span>}
      {Array(emptyStars)
        .fill("☆")
        .map((s, i) => (
          <span key={`empty-${i}`}>{s}</span>
        ))}
    </div>
  );
}

export default function IdentifyingScreen() {
  const [progressValues, setProgressValues] = useState(
    Array(steps.length).fill(0)
  );
  const navigate = useNavigate();

  useEffect(() => {
    if (process.env.NODE_ENV === "production") {
      amplitude.track("Identifying Screen Open");
    }

    const totalDuration = 10000; // 10 секунд на всю анимацию
    const stepDuration = totalDuration / steps.length;

    const startTime = Date.now();
    let animationFrameId;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const newProgressValues = steps.map((_, idx) => {
        const stepStartTime = idx * stepDuration;
        if (elapsed < stepStartTime) return 0;

        const stepProgress = Math.min(
          100,
          ((elapsed - stepStartTime) / stepDuration) * 100
        );
        return stepProgress;
      });

      setProgressValues(newProgressValues);

      if (elapsed < totalDuration) {
        animationFrameId = requestAnimationFrame(animate);
      } else {
        setTimeout(() => {
          if (process.env.NODE_ENV === "production") {
            amplitude.track("Quiz Step Complete", {
              step_number: 37,
              answers: "",
            });
          }
          navigate("/treatment");
        }, 500);
      }
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [navigate]);

  return (
    <AnimatedWrapper>
      <h2 className="text-center text-lg font-semibold my-4">
        Identifying skin type...
      </h2>

      {/* Steps List */}
      <div className="space-y-4 mb-6">
        {steps.map((step, idx) => (
          <div key={idx} className="relative">
            <div className="flex justify-between items-center">
              <p className="pb-2 text-base font-semibold text-black mb-2">
                {step}
              </p>
              <div className="flex items-center gap-2">
                {/* Крутящийся круг / галочка */}
                <div className="w-6 h-6 flex items-center justify-center">
                  {progressValues[idx] === 100 ? (
                    <motion.div 
                      className="w-6 h-6 border-2 border-pinkCustom rounded-full flex items-center justify-center text-xs font-bold text-pinkCustom"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      ✓
                    </motion.div>
                  ) : progressValues[idx] > 0 ? (
                    <motion.div 
                      className="w-6 h-6 border-2 border-pinkCustom border-t-transparent rounded-full"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    />
                  ) : (
                    <div className="w-6 h-6 border-2 border-gray-300 rounded-full" />
                  )}
                </div>

                {/* Текстовый процент */}
                <motion.span 
                  className="text-xs text-gray-600 min-w-[30px]"
                  key={progressValues[idx]}
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.2 }}
                >
                  {Math.round(progressValues[idx])}%
                </motion.span>
              </div>
            </div>
            
            {/* Прогресс-бар с Framer Motion */}
            <div className="w-full h-1.5 bg-gray-200 rounded-md overflow-hidden">
              <motion.div
                className="h-full bg-pinkCustom rounded-md"
                initial={{ width: 0 }}
                animate={{ width: `${progressValues[idx]}%` }}
                transition={{ 
                  duration: 0.3,
                  ease: "easeOut"
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Trust Text */}
      <div className="text-center text-2xl font-medium text-gray-800 border-y py-2 mb-4">
        Trusted by over 28,820 clients
      </div>

      {/* Reviews Slider */}
      <div className="relative overflow-visible">
        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          spaceBetween={16}
          slidesPerView={1.2}
          autoplay={{ delay: 4000 }}
          className="!overflow-visible relative px-4"
        >
          {reviews.map((review, idx) => (
            <SwiperSlide key={idx} className="!h-[200px] flex-shrink-0">
              <div className="h-full p-4 bg-gray-50 rounded-lg shadow border flex flex-col justify-between">
                <div>
                  <div className="flex justify-between items-center gap-2 mb-2">
                    <StarRating rating={review.rating} />
                    <div className="flex flex-col">
                      <span className="text-xs text-gray-500">
                        {review.date}
                      </span>
                      <span className="text-xs text-gray-500">
                        {review.city}
                      </span>
                    </div>
                  </div>
                  <h4 className="text-sm font-semibold mb-1">{review.title}</h4>
                  <p className="text-sm text-gray-700 line-clamp-4">
                    {review.text}
                  </p>
                </div>
                <div className="text-xs text-right text-gray-500 mt-2">
                  — {review.name}
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </AnimatedWrapper>
  );
}